import { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trash2, Grid3X3, Copy, Check, MapPin, Navigation, MousePointer2, Download, FileJson } from 'lucide-react';
import {
  getGeohashesForPolygon,
  getGeohashesNearPoint,
  getGeohashColor,
  PRECISION_DESCRIPTIONS,
  type GeohashData,
} from '@/lib/geohash';
import { useToast } from '@/hooks/useToast';

// Fix Leaflet default marker icon issue
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

type Mode = 'draw' | 'near';

export function GeohashMap() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const drawnItemsRef = useRef<L.FeatureGroup | null>(null);
  const geohashLayerRef = useRef<L.LayerGroup | null>(null);
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const radiusCircleRef = useRef<L.Circle | null>(null);

  const [mode, setMode] = useState<Mode>('near');
  const [precision, setPrecision] = useState(6);
  const [geohashes, setGeohashes] = useState<GeohashData[]>([]);
  const [drawnPolygon, setDrawnPolygon] = useState<[number, number][] | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [copiedHash, setCopiedHash] = useState<string | null>(null);

  // Near mode state - default to Austin, Texas
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>({
    lat: 30.2672,
    lng: -97.7431,
  });
  const [radius, setRadius] = useState(5000); // meters
  const [latInput, setLatInput] = useState('30.2672');
  const [lngInput, setLngInput] = useState('-97.7431');
  const [isLocating, setIsLocating] = useState(false);

  const { toast } = useToast();

  // Initialize the map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create the map - center on Austin, Texas (default location)
    const map = L.map(mapRef.current).setView([30.2672, -97.7431], 11);
    mapInstanceRef.current = map;

    // Add tile layer
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);

    // Create feature group for drawn items
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);
    drawnItemsRef.current = drawnItems;

    // Create layer group for geohash visualization
    const geohashLayer = L.layerGroup();
    map.addLayer(geohashLayer);
    geohashLayerRef.current = geohashLayer;

    // Initialize draw control (hidden by default, shown in draw mode)
    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: {
            color: '#6366f1',
            weight: 3,
            fillOpacity: 0.2,
          },
        },
        rectangle: {
          shapeOptions: {
            color: '#6366f1',
            weight: 3,
            fillOpacity: 0.2,
          },
        },
        circle: false,
        circlemarker: false,
        marker: false,
        polyline: false,
      },
      edit: {
        featureGroup: drawnItems,
        remove: true,
      },
    });
    drawControlRef.current = drawControl;

    // Handle draw created event
    map.on(L.Draw.Event.CREATED, (e: L.LeafletEvent) => {
      const event = e as L.DrawEvents.Created;
      const layer = event.layer;

      // Clear previous drawings
      drawnItems.clearLayers();

      // Add new layer
      drawnItems.addLayer(layer);

      // Extract polygon coordinates
      if (layer instanceof L.Polygon || layer instanceof L.Rectangle) {
        const latLngs = layer.getLatLngs()[0] as L.LatLng[];
        const polygon: [number, number][] = latLngs.map((ll) => [ll.lng, ll.lat]);
        setDrawnPolygon(polygon);
        setGeohashes([]);
      }
    });

    // Handle draw deleted event
    map.on(L.Draw.Event.DELETED, () => {
      setDrawnPolygon(null);
      setGeohashes([]);
      geohashLayerRef.current?.clearLayers();
    });

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Handle mode changes - toggle draw controls
  useEffect(() => {
    const map = mapInstanceRef.current;
    const drawControl = drawControlRef.current;
    if (!map || !drawControl) return;

    if (mode === 'draw') {
      map.addControl(drawControl);
    } else {
      map.removeControl(drawControl);
    }
  }, [mode]);

  // Handle map clicks for "near" mode
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const handleClick = (e: L.LeafletMouseEvent) => {
      if (mode !== 'near') return;

      const { lat, lng } = e.latlng;
      setSelectedLocation({ lat, lng });
      setLatInput(lat.toFixed(8));
      setLngInput(lng.toFixed(8));
      setGeohashes([]); // Clear previous results when selecting new location
    };

    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [mode]);

  // Update marker and radius circle when location changes
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing marker and circle
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (radiusCircleRef.current) {
      map.removeLayer(radiusCircleRef.current);
      radiusCircleRef.current = null;
    }

    // Add new marker and circle if location is selected
    if (selectedLocation && mode === 'near') {
      const marker = L.marker([selectedLocation.lat, selectedLocation.lng], {
        icon: L.divIcon({
          className: 'custom-marker',
          html: `<div class="w-6 h-6 bg-indigo-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
            <div class="w-2 h-2 bg-white rounded-full"></div>
          </div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      }).addTo(map);
      markerRef.current = marker;

      const circle = L.circle([selectedLocation.lat, selectedLocation.lng], {
        radius: radius,
        color: '#6366f1',
        fillColor: '#6366f1',
        fillOpacity: 0.1,
        weight: 2,
        dashArray: '5, 5',
      }).addTo(map);
      radiusCircleRef.current = circle;
    }
  }, [selectedLocation, radius, mode]);

  // Calculate geohashes for polygon mode
  const calculatePolygonGeohashes = useCallback(() => {
    if (!drawnPolygon || !geohashLayerRef.current) return;

    setIsCalculating(true);

    setTimeout(() => {
      try {
        const hashes = getGeohashesForPolygon(drawnPolygon, precision);

        if (hashes.length > 10000) {
          toast({
            title: 'Too many geohashes',
            description: `Found ${hashes.length} geohashes. Please use a lower precision level or draw a smaller region.`,
            variant: 'destructive',
          });
          setIsCalculating(false);
          return;
        }

        setGeohashes(hashes);
        visualizeGeohashes(hashes);

        toast({
          title: 'Geohashes calculated',
          description: `Found ${hashes.length} geohashes at precision ${precision}`,
        });
      } catch (error) {
        console.error('Error calculating geohashes:', error);
        toast({
          title: 'Error',
          description: 'Failed to calculate geohashes. Try a lower precision.',
          variant: 'destructive',
        });
      }

      setIsCalculating(false);
    }, 50);
  }, [drawnPolygon, precision, toast]);

  // Calculate geohashes for near mode
  const calculateNearGeohashes = useCallback(() => {
    if (!selectedLocation || !geohashLayerRef.current) return;

    setIsCalculating(true);

    setTimeout(() => {
      try {
        const hashes = getGeohashesNearPoint(
          selectedLocation.lat,
          selectedLocation.lng,
          radius,
          precision
        );

        if (hashes.length > 10000) {
          toast({
            title: 'Too many geohashes',
            description: `Found ${hashes.length} geohashes. Please use a lower precision level or smaller radius.`,
            variant: 'destructive',
          });
          setIsCalculating(false);
          return;
        }

        setGeohashes(hashes);
        visualizeGeohashes(hashes);

        toast({
          title: 'Geohashes near point',
          description: `Found ${hashes.length} geohashes within ${formatRadius(radius)}`,
        });
      } catch (error) {
        console.error('Error calculating geohashes:', error);
        toast({
          title: 'Error',
          description: 'Failed to calculate geohashes. Try a lower precision or smaller radius.',
          variant: 'destructive',
        });
      }

      setIsCalculating(false);
    }, 50);
  }, [selectedLocation, radius, precision, toast]);

  // Visualize geohashes on the map
  const visualizeGeohashes = useCallback((hashes: GeohashData[]) => {
    geohashLayerRef.current?.clearLayers();

    hashes.forEach((gh) => {
      const bounds: L.LatLngBoundsExpression = [
        [gh.bounds.minLat, gh.bounds.minLng],
        [gh.bounds.maxLat, gh.bounds.maxLng],
      ];

      const rect = L.rectangle(bounds, {
        color: getGeohashColor(gh.hash),
        weight: 1,
        fillOpacity: 0.3,
      });

      rect.bindTooltip(gh.hash, {
        permanent: false,
        direction: 'center',
        className: 'geohash-tooltip',
      });

      geohashLayerRef.current?.addLayer(rect);
    });
  }, []);

  // Clear everything
  const clearAll = useCallback(() => {
    drawnItemsRef.current?.clearLayers();
    geohashLayerRef.current?.clearLayers();
    setDrawnPolygon(null);
    setGeohashes([]);
    setSelectedLocation(null);
    setLatInput('');
    setLngInput('');

    if (markerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    if (radiusCircleRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(radiusCircleRef.current);
      radiusCircleRef.current = null;
    }
  }, []);

  // Copy hash to clipboard
  const copyHash = useCallback(async (hash: string) => {
    try {
      await navigator.clipboard.writeText(hash);
      setCopiedHash(hash);
      setTimeout(() => setCopiedHash(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, []);

  // Copy all hashes
  const copyAllHashes = useCallback(async () => {
    try {
      const allHashes = JSON.stringify(geohashes.map((gh) => gh.hash));
      await navigator.clipboard.writeText(allHashes);
      toast({
        title: 'Copied!',
        description: `${geohashes.length} geohashes copied to clipboard`,
      });
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }, [geohashes, toast]);

  // Download as GeoJSON
  const downloadGeoJSON = useCallback(() => {
    const geoJSON = {
      type: 'FeatureCollection',
      features: geohashes.map((gh) => ({
        type: 'Feature',
        properties: {
          geohash: gh.hash,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [[
            [gh.bounds.minLng, gh.bounds.minLat],
            [gh.bounds.maxLng, gh.bounds.minLat],
            [gh.bounds.maxLng, gh.bounds.maxLat],
            [gh.bounds.minLng, gh.bounds.maxLat],
            [gh.bounds.minLng, gh.bounds.minLat],
          ]],
        },
      })),
    };

    const blob = new Blob([JSON.stringify(geoJSON, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'geohashes.geojson';
    a.click();
    URL.revokeObjectURL(url);
  }, [geohashes]);

  // Download as CSV
  const downloadCSV = useCallback(() => {
    const csv = ['geohash,minLat,maxLat,minLng,maxLng']
      .concat(geohashes.map((gh) =>
        `${gh.hash},${gh.bounds.minLat},${gh.bounds.maxLat},${gh.bounds.minLng},${gh.bounds.maxLng}`
      ))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'geohashes.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [geohashes]);

  // Handle coordinate input
  const handleCoordinateSubmit = useCallback(() => {
    const lat = parseFloat(latInput);
    const lng = parseFloat(lngInput);

    if (isNaN(lat) || isNaN(lng)) {
      toast({
        title: 'Invalid coordinates',
        description: 'Please enter valid latitude and longitude values.',
        variant: 'destructive',
      });
      return;
    }

    if (lat < -90 || lat > 90) {
      toast({
        title: 'Invalid latitude',
        description: 'Latitude must be between -90 and 90.',
        variant: 'destructive',
      });
      return;
    }

    if (lng < -180 || lng > 180) {
      toast({
        title: 'Invalid longitude',
        description: 'Longitude must be between -180 and 180.',
        variant: 'destructive',
      });
      return;
    }

    setSelectedLocation({ lat, lng });
    setGeohashes([]); // Clear previous results

    // Pan map to location
    mapInstanceRef.current?.setView([lat, lng], 12);
  }, [latInput, lngInput, toast]);

  // Get user's geolocation
  const geolocateMe = useCallback(() => {
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation not supported',
        description: 'Your browser does not support geolocation.',
        variant: 'destructive',
      });
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setSelectedLocation({ lat: latitude, lng: longitude });
        setLatInput(latitude.toFixed(8));
        setLngInput(longitude.toFixed(8));
        setGeohashes([]); // Clear previous results
        mapInstanceRef.current?.setView([latitude, longitude], 12);
        setIsLocating(false);

        toast({
          title: 'Location found',
          description: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
        });
      },
      (error) => {
        setIsLocating(false);
        toast({
          title: 'Geolocation error',
          description: error.message,
          variant: 'destructive',
        });
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [toast]);

  // Format radius for display
  const formatRadius = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  // Handle mode switch
  const handleModeChange = (newMode: string) => {
    setMode(newMode as Mode);
    clearAll();
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <header className="border-b bg-card/80 backdrop-blur-sm px-4 py-3">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <MapPin className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Geohash Mapper</h1>
              <p className="text-xs text-muted-foreground">Calculate geohashes for any region</p>
            </div>
          </div>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map Container */}
        <div className="flex-1 relative">
          <div ref={mapRef} className="absolute inset-0" />

          {/* Floating Controls */}
          <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-3 max-w-sm">
            <Card className="shadow-xl border-0 bg-card/95 backdrop-blur-sm">
              <CardContent className="p-4 space-y-4">
                {/* Mode Tabs */}
                <Tabs value={mode} onValueChange={handleModeChange} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="near" className="flex items-center gap-2">
                      <Navigation className="h-4 w-4" />
                      Near
                    </TabsTrigger>
                    <TabsTrigger value="draw" className="flex items-center gap-2">
                      <MousePointer2 className="h-4 w-4" />
                      Draw
                    </TabsTrigger>
                  </TabsList>

                  {/* Near Mode Controls */}
                  <TabsContent value="near" className="space-y-4 mt-4">
                    {/* Coordinate Input */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Enter a coordinate</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Latitude</Label>
                          <Input
                            type="text"
                            placeholder="30.2699"
                            value={latInput}
                            onChange={(e) => setLatInput(e.target.value)}
                            className="h-8 text-sm font-mono"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Longitude</Label>
                          <Input
                            type="text"
                            placeholder="-97.7416"
                            value={lngInput}
                            onChange={(e) => setLngInput(e.target.value)}
                            className="h-8 text-sm font-mono"
                          />
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={handleCoordinateSubmit}
                      >
                        Set Location
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <div className="flex-1 h-px bg-border" />
                      <span>Or click on the map</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>

                    {/* Geolocate Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={geolocateMe}
                      disabled={isLocating}
                    >
                      {isLocating ? (
                        <span className="flex items-center gap-2">
                          <span className="h-4 w-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                          Locating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Navigation className="h-4 w-4" />
                          Geolocate me
                        </span>
                      )}
                    </Button>

                    {/* Radius Slider */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Radius</Label>
                        <Badge variant="secondary" className="font-mono text-xs">
                          {formatRadius(radius)}
                        </Badge>
                      </div>
                      <Slider
                        value={[radius]}
                        onValueChange={([v]) => setRadius(v)}
                        min={100}
                        max={50000}
                        step={100}
                        className="w-full"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>100m</span>
                        <span>50km</span>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Draw Mode Controls */}
                  <TabsContent value="draw" className="mt-4">
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Use the draw tools on the left side of the map to select a region
                    </p>
                  </TabsContent>
                </Tabs>

                {/* Precision Selector (common to both modes) */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Grid3X3 className="h-4 w-4 text-indigo-500" />
                    <Label className="text-xs font-medium">Precision Level</Label>
                  </div>
                  <Select
                    value={precision.toString()}
                    onValueChange={(v) => setPrecision(parseInt(v))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRECISION_DESCRIPTIONS).map(([level, desc]) => (
                        <SelectItem key={level} value={level}>
                          <span className="font-medium">Level {level}</span>
                          <span className="text-muted-foreground ml-2 text-xs">{desc}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={mode === 'near' ? calculateNearGeohashes : calculatePolygonGeohashes}
                    disabled={
                      (mode === 'near' && !selectedLocation) ||
                      (mode === 'draw' && !drawnPolygon) ||
                      isCalculating
                    }
                    className="flex-1 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                  >
                    {isCalculating ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Calculating...
                      </span>
                    ) : (
                      'Get Hashes'
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={clearAll}
                    disabled={!drawnPolygon && geohashes.length === 0 && !selectedLocation}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                {/* Help text */}
                {mode === 'near' && !selectedLocation && (
                  <p className="text-xs text-muted-foreground text-center">
                    Click on the map or enter coordinates to select a location
                  </p>
                )}
                {mode === 'draw' && !drawnPolygon && (
                  <p className="text-xs text-muted-foreground text-center">
                    Use the draw tools on the map to select a region
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Results Sidebar */}
        {geohashes.length > 0 && (
          <div className="w-96 border-l bg-card flex flex-col">
            <div className="p-4 border-b space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold">Geohashes near:</h2>
                  <p className="text-sm text-muted-foreground font-mono">
                    {geohashes.length} hashes at level {precision}
                  </p>
                </div>
                <Badge variant="outline" className="font-bold text-lg">
                  {geohashes.length}
                </Badge>
              </div>

              {/* Result display as JSON array preview */}
              <div className="bg-muted/50 rounded-lg p-3 max-h-32 overflow-auto">
                <code className="text-xs font-mono text-muted-foreground break-all">
                  {JSON.stringify(geohashes.map((gh) => gh.hash))}
                </code>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" size="sm" onClick={copyAllHashes}>
                  <Copy className="h-4 w-4 mr-1" />
                  Copy
                </Button>
                <Button variant="outline" size="sm" onClick={downloadGeoJSON}>
                  <FileJson className="h-4 w-4 mr-1" />
                  GeoJSON
                </Button>
                <Button variant="outline" size="sm" onClick={downloadCSV}>
                  <Download className="h-4 w-4 mr-1" />
                  CSV
                </Button>
              </div>
            </div>

            <ScrollArea className="flex-1">
              <div className="p-4 space-y-2">
                {geohashes.map((gh) => (
                  <div
                    key={gh.hash}
                    className="flex items-center justify-between p-2 rounded-lg border bg-background hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="h-4 w-4 rounded"
                        style={{ backgroundColor: getGeohashColor(gh.hash) }}
                      />
                      <Badge variant="secondary" className="font-mono text-xs">
                        {gh.hash}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => copyHash(gh.hash)}
                    >
                      {copiedHash === gh.hash ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
